import { Rule } from 'antd/lib/form';
import { Form, Select } from 'antd';
import moment from 'moment';
import { NamePath } from 'rc-field-form/lib/interface';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { FormItem } from '@xbcb/form-item-components';
import { formatHTS } from '@xbcb/hts-client-utils';
import { useBundle } from '@amzn/react-arb-tools';
import { safeGetMessage } from '@xbcb/ui-utils';
import HTSMask from 'libs/htsMask';
import { getArrivalDateMoment, getHtsSuggestions } from 'libs/htsHelpers';
import { validateEffectiveHTS } from 'libs/validateEffectiveHTS';
import { useArrivalDate, usePrevious } from 'libs/hooks';
import { RootState } from 'reducers';

export interface HTSNumberV2Props {
  field: NamePath;
  fullNamePath: NamePath;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  onSelect?: (value: string) => void;
  onClear?: () => void;
  excludeValues?: string[];
}

const HTSNumberV2: React.FC<HTSNumberV2Props> = ({
  field,
  fullNamePath,
  label,
  excludeValues = [],
  required = false,
  disabled,
  readOnly,
  onSelect,
  onClear,
}) => {
  const [bundle] = useBundle('components.LineItemTariffDisplayV2');
  const resolvedLabel =
    label ||
    safeGetMessage(bundle, 'hts_number_label', undefined, 'HTS Number');
  const theme = useTheme();
  const form = Form.useFormInstance();
  const arrivalDate = useArrivalDate();
  const htsHierarchy = useSelector(
    (state: RootState) => state.hts.usHtsHierarchy.htsHierarchy,
  );
  const [options, setOptions] = useState<{ value: string; label: string }[]>(
    [],
  );
  const [searchValue, setSearchValue] = useState('');
  const htsMask = useRef(new HTSMask());

  const htsNumber = Form.useWatch(fullNamePath, form);
  const previousHtsNumber = usePrevious(htsNumber);

  const htsEffectiveDate = moment(arrivalDate);

  const getOptions = (value: string) => {
    const raw = formatHTS(value);
    const suggestions = getHtsSuggestions(
      raw,
      htsHierarchy,
      htsEffectiveDate || moment(),
    );
    const excluded = new Set(excludeValues.map(formatHTS));
    const filtered = suggestions
      .filter((htsNumber: string) => !excluded.has(formatHTS(htsNumber)))
      .sort();
    setOptions(
      raw
        ? filtered.map((htsNumber: string) => ({
            value: htsNumber,
            label: htsNumber,
          }))
        : [],
    );
  };

  const handleChange = (value: string) => {
    return htsMask.current.handleChange(value) || '';
  };

  const isValidHts = (value: string): boolean => {
    try {
      validateEffectiveHTS(
        value,
        getArrivalDateMoment(form) || moment(),
        htsHierarchy,
      );
      return true;
    } catch {
      return false;
    }
  };

  const handleSelect = (value: string | undefined) => {
    if (value) {
      getOptions(value);
      setSearchValue('');
    }
    if (
      onSelect &&
      value &&
      isValidHts(value) &&
      formatHTS(value) !== formatHTS(previousHtsNumber)
    ) {
      onSelect(value);
    }
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    if (open && htsNumber) {
      setSearchValue(htsNumber);
      getOptions(htsNumber);
    } else {
      setSearchValue('');
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    getOptions(value);
  };

  /* istanbul ignore next */
  const validateHTS = async (_: Rule, value: string) => {
    if (!value && !required) return;
    const date =
      form.getFieldValue(['arrival', 'importDate']) || moment().format();
    await validateEffectiveHTS(value, moment(date), htsHierarchy);
  };

  return (
    <FormItem
      dependencies={[
        ['arrival', 'importDate'],
        ['arrival', 'inBond', 'estimatedEntryDate'],
      ]}
      getValueFromEvent={handleChange}
      label={resolvedLabel}
      name={field}
      rules={[{ validator: validateHTS, required, message: ' ' }]}
      $itemSize={theme.size.short}
      $readOnly={readOnly}
    >
      <Select
        options={options}
        showSearch
        allowClear
        dropdownMatchSelectWidth={false}
        disabled={disabled}
        onSelect={handleSelect}
        searchValue={searchValue}
        onSearch={handleSearch}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        onClear={onClear}
        placeholder={safeGetMessage(
          bundle,
          'hts_number_placeholder',
          undefined,
          'NNNN.NN.NNNN',
        )}
        notFoundContent={safeGetMessage(
          bundle,
          'hts_number_not_found',
          undefined,
          'None found',
        )}
        aria-label="HTS Number"
        filterOption={false}
      />
    </FormItem>
  );
};

export default HTSNumberV2;
